import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  consoleQueries,
  getApiErrorMessage,
  tenantsApi,
  tenantQueries,
  queryKeys,
} from '@milliswift/api-client'
import {
  Button,
  Callout,
  DescriptionList,
  FormSection,
  PreviewDataBadge,
  SectionHeader,
  TagInput,
  useToast,
} from '@milliswift/ui'
import { ShieldAlert } from 'lucide-react'
import { useState } from 'react'
import { useDb } from '../lib/db'

/**
 * Console settings.
 *
 * The connection allowlist is real (PUT /account/ip-allowlist, admin-only on the
 * platform). Database-scoped settings are preview data until the console
 * endpoints ship (docs/PLATFORM_API_GAPS.md).
 */
export default function SettingsPage() {
  const { databaseId } = useDb()
  const toast = useToast()
  const queryClient = useQueryClient()

  const account = useQuery(tenantQueries.account())
  const databases = useQuery(consoleQueries.databases())

  /** Local edit state; null means "not editing, show the server's value". */
  const [cidrs, setCidrs] = useState<string[] | null>(null)

  const serverAllowlist = account.data?.ip_allowlist ?? []
  const editing = cidrs ?? serverAllowlist
  const dirty = cidrs !== null && JSON.stringify(cidrs) !== JSON.stringify(serverAllowlist)

  const saveAllowlist = useMutation({
    mutationFn: () => tenantsApi.setIPAllowlist({ cidrs: editing }),
    onSuccess: () => {
      toast.success('Allowlist saved')
      setCidrs(null)
      void queryClient.invalidateQueries({ queryKey: queryKeys.account })
    },
    onError: (error) => toast.error('Could not save', getApiErrorMessage(error)),
  })

  const activeDb = databases.data?.find((db) => db.database_id === databaseId) ?? databases.data?.[0]

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Settings"
        description="Connection security and database configuration."
      />

      {account.isPending ? null : account.isError ? (
        <Callout tone="danger" title="Could not load the account">
          {getApiErrorMessage(account.error)}
        </Callout>
      ) : (
        <FormSection
          title="Connection allowlist"
          description="Which networks API keys may connect from. Changing it is an admin action."
          action={
            <Button
              size="sm"
              disabled={!dirty}
              loading={saveAllowlist.isPending}
              onClick={() => saveAllowlist.mutate()}
            >
              Save
            </Button>
          }
        >
          <TagInput
            value={editing}
            onValueChange={setCidrs}
            validate={(value) =>
              /^\d{1,3}(\.\d{1,3}){3}\/\d{1,2}$/.test(value)
                ? null
                : 'Enter a CIDR range like 10.0.0.0/8'
            }
            placeholder="203.0.113.0/24"
          />
          {editing.length === 0 ? (
            <Callout tone="warning">
              An empty list accepts connections from anywhere. Restrict it for production accounts.
            </Callout>
          ) : (
            <p className="text-caption text-muted">
              {editing.length} range{editing.length === 1 ? '' : 's'} allowed.
            </p>
          )}
        </FormSection>
      )}

      <FormSection
        title="Active database"
        description="Read-only until the console endpoints ship."
        action={<PreviewDataBadge />}
      >
        <DescriptionList
          columns={2}
          items={[
            {
              term: 'Database ID',
              detail: <span className="font-mono">{activeDb?.database_id ?? '—'}</span>,
            },
            { term: 'Name', detail: activeDb?.name ?? '—' },
            { term: 'Region', detail: activeDb?.region ?? '—' },
            { term: 'Status', detail: activeDb?.status ?? '—' },
          ]}
        />
      </FormSection>

      <div className="rounded-card border border-danger/30 bg-surface p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-danger-text">
          <ShieldAlert className="h-4 w-4" aria-hidden />
          Danger zone
        </h2>
        <p className="mt-1 text-caption text-muted">
          Deleting a database destroys its data, snapshots and configuration. Available once the
          console endpoints ship.
        </p>
        <Button variant="danger" size="sm" className="mt-4" disabled>
          Delete this database
        </Button>
      </div>
    </div>
  )
}
